"use client";

import { useState, type ReactNode } from "react";
import { generateKundali } from "@/lib/api/kundali";
import type { KundaliResponse } from "@/lib/api/kundali";
import BirthDetailsForm from "@/components/BirthDetailsForm";
import { trackCalculatorUsed } from "@/lib/analytics-free-tools";
import { DashaResult, DashaSeoContent } from "./_sections";

export default function DashaCalculatorClient({
  defaultLanguage = "en",
  crossLinks,
}: {
  defaultLanguage?: "en" | "hi";
  crossLinks: ReactNode;
}) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | undefined>();
  const [result, setResult] = useState<KundaliResponse | null>(null);
  const hi = defaultLanguage === "hi";

  async function handleSubmit(data: Parameters<typeof generateKundali>[0]) {
    setLoading(true);
    setError(undefined);
    try {
      const response = await generateKundali({ ...data, skip_interpretation: true });
      if (!response.dasha) {
        throw new Error(
          hi
            ? "Dasha calculation abhi available nahi hai. Kripya dobara try karein."
            : "Dasha calculation is unavailable right now. Please try again."
        );
      }
      setResult(response);
      trackCalculatorUsed({
        calculator: "dasha-calculator",
        language: defaultLanguage,
        source_path: hi ? "/hi/dasha-calculator" : "/dasha-calculator",
        has_time: response.birth_time_known,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Something went wrong");
    } finally {
      setLoading(false);
    }
  }

  if (result) {
    return (
      <DashaResult result={result} defaultLanguage={defaultLanguage} crossLinks={crossLinks} />
    );
  }

  return (
    <div className="sacred-bg min-h-[calc(100vh-130px)]">
      <div className="mx-auto max-w-lg px-4 sm:px-6 pt-12 sm:pt-20 pb-6">
        <div className="text-center">
          <p
            className="text-[11px] font-semibold uppercase tracking-[0.25em] mb-4"
            style={{ color: "var(--ish-accent)" }}
          >
            Vimshottari Dasha System
          </p>
          <h1
            className="text-[32px] sm:text-[42px] leading-[1.15] font-bold"
            style={{
              fontFamily: "var(--font-pt-serif), PT Serif, Georgia, serif",
              color: "var(--ish-dark)",
            }}
          >
            Free Dasha <span style={{ color: "var(--ish-accent)" }}>Calculator</span>
          </h1>
          <p
            className="mt-4 text-[15px] leading-relaxed max-w-md mx-auto"
            style={{ color: "var(--ish-text-muted)" }}
          >
            Calculate your Vimshottari Mahadasha sequence based on your Moon nakshatra. Discover
            which planetary periods shape your life.
          </p>

          <div
            className="mt-5 flex flex-wrap justify-center gap-x-6 gap-y-2 text-[11px] font-medium"
            style={{ color: "var(--ish-text-faint)" }}
          >
            <span className="flex items-center gap-1.5">
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
              </svg>
              Swiss Ephemeris
            </span>
            <span className="flex items-center gap-1.5">
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <circle cx="12" cy="12" r="10" />
                <path d="M12 6v6l4 2" />
              </svg>
              120-Year Cycle
            </span>
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-md px-4 sm:px-6 pb-8">
        <BirthDetailsForm
          rememberBirthDetails
          onSubmit={handleSubmit}
          loading={loading}
          error={error}
          submitLabel={hi ? "Dasha calculate karein" : "Calculate Dasha"}
          language={defaultLanguage}
          analyticsToolName="dasha-calculator"
        />
      </div>

      <DashaSeoContent crossLinks={crossLinks} />
    </div>
  );
}
